import React, { useState } from 'react';
import { Eye, EyeOff, Loader2, AlertCircle, CheckCircle, Info, AlertTriangle } from 'lucide-react';

const T = {
  navy:'#071428',navyMid:'#0d2044',navySoft:'#152d5a',
  blue:'#1a5cff',blueMid:'#0f4be8',blueLight:'#4d7eff',bluePale:'#e8f0ff',
  teal:'#00b8a0',tealSoft:'#e0faf6',
  red:'#dc2626',redSoft:'#fef2f2',
  amber:'#d97706',amberSoft:'#fffbeb',
  green:'#059669',greenSoft:'#ecfdf5',
  g50:'#f8fafc',g100:'#f1f5f9',g200:'#e2e8f0',g300:'#cbd5e1',
  g400:'#94a3b8',g500:'#64748b',g700:'#334155',g900:'#0f172a',
  white:'#ffffff',
};

const BTN_VARIANTS = {
  primary:{bg:`linear-gradient(135deg,#1a5cff 0%,#0f4be8 100%)`,color:'#fff',border:'none',shadow:'0 4px 14px rgba(26,92,255,0.35),inset 0 1px 0 rgba(255,255,255,0.15)',hoverBg:`linear-gradient(135deg,#4d7eff 0%,#1a5cff 100%)`,hoverShadow:'0 6px 20px rgba(26,92,255,0.45)'},
  secondary:{bg:'#fff',color:T.navy,border:`1.5px solid ${T.g200}`,shadow:'0 1px 4px rgba(0,0,0,0.06)',hoverBg:T.g50,hoverShadow:'0 3px 10px rgba(0,0,0,0.1)'},
  ghost:{bg:'transparent',color:T.blue,border:'none',shadow:'none',hoverBg:T.bluePale,hoverShadow:'none'},
  outline:{bg:'transparent',color:T.blue,border:`1.5px solid ${T.blue}`,shadow:'none',hoverBg:T.bluePale,hoverShadow:'0 2px 10px rgba(26,92,255,0.15)'},
  teal:{bg:`linear-gradient(135deg,#00b8a0 0%,#009d8a 100%)`,color:'#fff',border:'none',shadow:'0 4px 14px rgba(0,184,160,0.35)',hoverBg:`linear-gradient(135deg,#00cdb3 0%,#00b8a0 100%)`,hoverShadow:'0 6px 20px rgba(0,184,160,0.45)'},
  danger:{bg:`linear-gradient(135deg,#dc2626 0%,#b91c1c 100%)`,color:'#fff',border:'none',shadow:'0 4px 14px rgba(220,38,38,0.3)',hoverBg:`linear-gradient(135deg,#ef4444 0%,#dc2626 100%)`,hoverShadow:'0 6px 20px rgba(220,38,38,0.4)'},
  navy:{bg:`linear-gradient(135deg,#152d5a 0%,#0d2044 100%)`,color:'#fff',border:'none',shadow:'0 4px 14px rgba(7,20,40,0.4)',hoverBg:`linear-gradient(135deg,#1e3d6e 0%,#152d5a 100%)`,hoverShadow:'0 6px 20px rgba(7,20,40,0.5)'},
};
const BTN_SIZES = {
  xs:{padding:'5px 12px',fontSize:12,radius:8,gap:5,icon:12},
  sm:{padding:'7px 16px',fontSize:13,radius:9,gap:6,icon:14},
  md:{padding:'10px 20px',fontSize:14,radius:10,gap:8,icon:15},
  lg:{padding:'13px 26px',fontSize:15,radius:11,gap:9,icon:16},
  xl:{padding:'16px 32px',fontSize:16,radius:12,gap:10,icon:17},
};

export function Btn({children,variant='primary',size='md',loading,disabled,icon:Icon,iconRight,fullWidth,style,onClick,type='button',...rest}){
  const [h,setH]=useState(false);
  const [p,setP]=useState(false);
  const sz=BTN_SIZES[size]||BTN_SIZES.md;
  const v=BTN_VARIANTS[variant]||BTN_VARIANTS.primary;
  const off=disabled||loading;
  return(
    <button type={type} disabled={off} onClick={onClick}
      onMouseEnter={()=>setH(true)} onMouseLeave={()=>{setH(false);setP(false);}}
      onMouseDown={()=>setP(true)} onMouseUp={()=>setP(false)}
      style={{display:'inline-flex',alignItems:'center',justifyContent:'center',
        gap:sz.gap,padding:sz.padding,fontSize:sz.fontSize,fontWeight:600,
        fontFamily:"'DM Sans',system-ui,sans-serif",letterSpacing:'0.01em',
        borderRadius:sz.radius,border:v.border||'none',
        background:h&&!off?v.hoverBg:v.bg,color:v.color,
        boxShadow:h&&!off?v.hoverShadow:v.shadow,
        cursor:off?'not-allowed':'pointer',opacity:off?0.55:1,
        transform:p&&!off?'scale(0.97) translateY(1px)':'scale(1) translateY(0)',
        transition:'all 0.18s cubic-bezier(0.4,0,0.2,1)',outline:'none',
        userSelect:'none',whiteSpace:'nowrap',width:fullWidth?'100%':undefined,
        ...style}}
      {...rest}>
      {loading?<Loader2 size={sz.icon} style={{animation:'spin 0.8s linear infinite'}}/>:(Icon&&!iconRight)?<Icon size={sz.icon} strokeWidth={2.2}/>:null}
      {children}
      {Icon&&iconRight&&!loading&&<Icon size={sz.icon} strokeWidth={2.2}/>}
      <style>{`@keyframes spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}`}</style>
    </button>
  );
}

export function TextInput({label,error,hint,icon:Icon,type='text',required,disabled,style,...props}){
  const [focused,setFocused]=useState(false);
  const [showPw,setShowPw]=useState(false);
  const isPassword=type==='password';
  const inputType=isPassword?(showPw?'text':'password'):type;
  const borderColor=error?T.red:focused?T.blue:T.g200;
  const shadow=error?'rgba(220,38,38,0.12)':focused?'rgba(26,92,255,0.12)':'transparent';
  return(
    <div style={{width:'100%',...style}}>
      {label&&<label style={{display:'flex',alignItems:'center',gap:5,fontSize:13,fontWeight:600,color:error?T.red:T.g700,marginBottom:7,fontFamily:"'DM Sans',sans-serif"}}>
        {label}{required&&<span style={{color:T.red,fontSize:12}}>*</span>}
      </label>}
      <div style={{position:'relative'}}>
        {Icon&&<div style={{position:'absolute',left:13,top:'50%',transform:'translateY(-50%)',color:focused?T.blue:T.g400,pointerEvents:'none',transition:'color 0.2s',display:'flex'}}><Icon size={16} strokeWidth={2}/></div>}
        <input type={inputType} disabled={disabled}
          style={{width:'100%',padding:`11px ${isPassword?'42px':'14px'} 11px ${Icon?'42px':'14px'}`,fontSize:14,fontFamily:"'DM Sans',sans-serif",fontWeight:400,color:T.g900,background:disabled?T.g50:T.white,border:`1.5px solid ${borderColor}`,borderRadius:10,outline:'none',transition:'all 0.2s ease',boxShadow:`0 0 0 3px ${shadow}`,letterSpacing:isPassword&&!showPw?'0.15em':'0'}}
          onFocus={()=>setFocused(true)} onBlur={()=>setFocused(false)}
          {...props}/>
        {isPassword&&<button type="button" onClick={()=>setShowPw(v=>!v)} tabIndex={-1}
          style={{position:'absolute',right:13,top:'50%',transform:'translateY(-50%)',background:'none',border:'none',cursor:'pointer',color:T.g400,padding:4,display:'flex',borderRadius:6,transition:'color 0.2s'}}
          onMouseEnter={e=>e.currentTarget.style.color=T.blue}
          onMouseLeave={e=>e.currentTarget.style.color=T.g400}>
          {showPw?<EyeOff size={16} strokeWidth={2}/>:<Eye size={16} strokeWidth={2}/>}
        </button>}
      </div>
      {error&&<p style={{display:'flex',alignItems:'center',gap:5,color:T.red,fontSize:12,marginTop:5,fontWeight:500}}><AlertCircle size={12}/>{error}</p>}
      {hint&&!error&&<p style={{color:T.g400,fontSize:12,marginTop:5}}>{hint}</p>}
    </div>
  );
}

export function Alert({type='info',children,style}){
  const cfg={
    info:{bg:T.bluePale,border:`${T.blue}30`,color:T.blue,Icon:Info,bar:T.blue},
    success:{bg:T.greenSoft,border:`${T.green}30`,color:T.green,Icon:CheckCircle,bar:T.green},
    error:{bg:T.redSoft,border:`${T.red}30`,color:T.red,Icon:AlertCircle,bar:T.red},
    warning:{bg:T.amberSoft,border:`${T.amber}30`,color:T.amber,Icon:AlertTriangle,bar:T.amber},
  };
  const c=cfg[type]||cfg.info;
  return(
    <div style={{display:'flex',alignItems:'flex-start',gap:10,background:c.bg,border:`1px solid ${c.border}`,borderLeft:`3px solid ${c.bar}`,borderRadius:10,padding:'12px 14px',...style}}>
      <c.Icon size={15} style={{color:c.color,flexShrink:0,marginTop:1}} strokeWidth={2.2}/>
      <span style={{fontSize:13.5,fontWeight:500,color:c.color,lineHeight:1.55}}>{children}</span>
    </div>
  );
}

export function Card({children,style}){return<div style={{background:T.white,borderRadius:16,border:`1px solid ${T.g200}`,boxShadow:'0 1px 4px rgba(0,0,0,0.06)',  ...style}}>{children}</div>;}

export function Section({title,children,style}){
  return(
    <div style={{background:T.white,borderRadius:16,padding:'22px 26px',border:`1px solid ${T.g200}`,boxShadow:'0 1px 4px rgba(0,0,0,0.05)',marginBottom:20,...style}}>
      {title&&<h3 style={{fontSize:15,fontWeight:700,color:'#071428',marginBottom:18,paddingBottom:14,borderBottom:`1px solid ${T.g100}`,fontFamily:"'DM Sans',sans-serif"}}>{title}</h3>}
      {children}
    </div>
  );
}

export function Badge({children,color='blue'}){
  const m={blue:{bg:T.bluePale,c:T.blue},green:{bg:T.greenSoft,c:T.green},red:{bg:T.redSoft,c:T.red},amber:{bg:T.amberSoft,c:T.amber},teal:{bg:T.tealSoft,c:T.teal},gray:{bg:T.g100,c:T.g500},navy:{bg:'#e8edf7',c:T.navySoft}};
  const s=m[color]||m.blue;
  return<span style={{background:s.bg,color:s.c,padding:'3px 10px',borderRadius:20,fontSize:12,fontWeight:700,display:'inline-block',fontFamily:"'DM Sans',sans-serif"}}>{children}</span>;
}

export function Spinner({size=28}){
  return(
    <div style={{display:'flex',justifyContent:'center',alignItems:'center',padding:40}}>
      <div style={{width:size,height:size,borderRadius:'50%',border:`2.5px solid ${T.g200}`,borderTopColor:T.blue,animation:'uiSpin 0.75s linear infinite'}}/>
      <style>{`@keyframes uiSpin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}`}</style>
    </div>
  );
}

export function Modal({open,onClose,title,children,maxWidth=640}){
  if(!open)return null;
  return(
    <div style={{position:'fixed',inset:0,background:'rgba(7,20,40,0.6)',backdropFilter:'blur(4px)',zIndex:1000,display:'flex',alignItems:'center',justifyContent:'center',padding:24}} onClick={onClose}>
      <div style={{background:T.white,borderRadius:20,width:'100%',maxWidth,maxHeight:'90vh',overflowY:'auto',boxShadow:'0 24px 80px rgba(0,0,0,0.3)',animation:'modalPop 0.22s cubic-bezier(0.34,1.56,0.64,1)'}} onClick={e=>e.stopPropagation()}>
        <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'20px 26px',borderBottom:`1px solid ${T.g100}`}}>
          <h2 style={{fontSize:17,fontWeight:700,color:'#071428',fontFamily:"'DM Sans',sans-serif"}}>{title}</h2>
          <button onClick={onClose} style={{background:T.g100,border:'none',cursor:'pointer',color:T.g500,borderRadius:8,width:30,height:30,display:'flex',alignItems:'center',justifyContent:'center',fontSize:16,transition:'all 0.15s'}} onMouseEnter={e=>{e.currentTarget.style.background=T.g200;e.currentTarget.style.color=T.g900;}} onMouseLeave={e=>{e.currentTarget.style.background=T.g100;e.currentTarget.style.color=T.g500;}}>✕</button>
        </div>
        <div style={{padding:'22px 26px'}}>{children}</div>
      </div>
      <style>{`@keyframes modalPop{from{opacity:0;transform:scale(0.93) translateY(10px)}to{opacity:1;transform:scale(1) translateY(0)}}`}</style>
    </div>
  );
}

export function PageHeader({title,subtitle,actions}){
  return(
    <div style={{marginBottom:28,display:'flex',alignItems:'flex-end',justifyContent:'space-between',flexWrap:'wrap',gap:14}}>
      <div>
        <h1 style={{fontSize:26,fontWeight:700,color:'#071428',fontFamily:"'DM Serif Display',serif",letterSpacing:'-0.4px',lineHeight:1.15}}>{title}</h1>
        {subtitle&&<p style={{color:'#64748b',fontSize:14,marginTop:5,fontFamily:"'DM Sans',sans-serif"}}>{subtitle}</p>}
      </div>
      {actions&&<div style={{display:'flex',gap:10,flexWrap:'wrap',alignItems:'center'}}>{actions}</div>}
    </div>
  );
}

export function Table({headers,rows,abnormalFn}){
  return(
    <div style={{overflowX:'auto',borderRadius:10,border:`1px solid #e2e8f0`}}>
      <table style={{width:'100%',borderCollapse:'collapse',fontSize:14}}>
        <thead><tr style={{background:'#071428'}}>
          {headers.map((h,i)=><th key={i} style={{padding:'11px 16px',textAlign:'left',color:'#fff',fontWeight:600,fontSize:13,fontFamily:"'DM Sans',sans-serif"}}>{h}</th>)}
        </tr></thead>
        <tbody>{rows.map((row,ri)=>(
          <tr key={ri} style={{borderBottom:'1px solid #f1f5f9',background:ri%2===0?'#fff':'#f8fafc'}}>
            {row.map((cell,ci)=><td key={ci} style={{padding:'10px 16px',color:abnormalFn&&ci===1&&!abnormalFn(row)?'#dc2626':'#334155',fontWeight:abnormalFn&&ci===1&&!abnormalFn(row)?700:ci===0?600:400,fontFamily:"'DM Sans',sans-serif"}}>{cell}</td>)}
          </tr>
        ))}</tbody>
      </table>
    </div>
  );
}
